import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { TextField, Grid, Autocomplete, CircularProgress, Typography, Box, Divider, } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import LeadDetailApi from "../../../infrastructure/api/pages/lead-detail-api";
import { useTranslation } from "react-i18next";
import CustomModal from "../modal/CustomModal";
import { MagnifyingGlass, UserPlus, User, EnvelopeSimple, Phone, Briefcase, } from "@phosphor-icons/react";
const CompanyInfoModal = ({ open, onClose, GetLeadContacts, initialFormData, data, }) => {
    const [saving, setSaving] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedExisting, setSelectedExisting] = useState(null);
    const [searchInputValue, setSearchInputValue] = useState("");
    const { t: translate } = useTranslation();
    const hiddenButtonRef = useRef(null);
    const debounceTimer = useRef(null);
    const handleSaveButtonClick = () => {
        if (hiddenButtonRef.current) {
            hiddenButtonRef.current.click();
        }
    };
    const validationSchema = yup.object({
        name: yup.string().required(translate("NameRequired")),
        email: yup.string().email(translate("InvalidEmail")),
    });
    const { reset, handleSubmit, control, register, setValue, formState: { errors }, } = useForm({
        resolver: yupResolver(validationSchema),
    });
    const onCloseModal = () => {
        reset(initialFormData);
        setSelectedExisting(null);
        setSearchResults([]);
        setHasSearched(false);
        setSearchInputValue("");
        onClose();
    };
    const handleSearchContacts = (query) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        if (!query || query.length < 2) {
            setSearchResults([]);
            setHasSearched(false);
            return;
        }
        debounceTimer.current = window.setTimeout(() => {
            setSearching(true);
            LeadDetailApi.searchContacts(query)
                .then((response) => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    setSearchResults(response.data);
                }
                else {
                    setSearchResults([]);
                }
                setHasSearched(true);
            })
                .catch(() => {
                setSearchResults([]);
                setHasSearched(true);
            })
                .finally(() => {
                setSearching(false);
            });
        }, 300);
    };
    const handleSelectExistingContact = (contact) => {
        setSelectedExisting(contact);
        if (contact) {
            const selectedLabel = contact.name || contact.email || "";
            setSearchInputValue(selectedLabel);
            setHasSearched(false);
            setSearchResults([]);
            setValue("id", contact.id);
            setValue("name", contact.name || "");
            setValue("function", contact.function || "");
            setValue("phoneNumber", contact.phoneNumber || "");
            setValue("email", contact.email || "");
        }
        else {
            reset(initialFormData);
        }
    };
    const cleanGuidFields = (data) => {
        return Object.assign(Object.assign({}, data), { id: data.id && data.id.trim() !== "" ? data.id : undefined, leadContactId: data.leadContactId && data.leadContactId.trim() !== ""
                ? data.leadContactId
                : undefined, leadId: data.leadId && data.leadId.trim() !== "" ? data.leadId : undefined });
    };
    const showContactSaveToast = (responseMessage, fallbackKey) => {
        const messageMap = {
            "Contact created and linked to lead successfully.": "ContactCreatedAndLinkedSuccessfully",
            "Existing contact linked to this lead successfully.": "ExistingContactLinkedToLeadSuccessfully",
            "Contact already linked to this lead. Details updated successfully.": "ContactAlreadyLinkedAndUpdatedSuccessfully",
            "Contact updated successfully.": "ContactUpdatedSuccessfully",
            "Contact matched existing record and relinked successfully.": "ContactMatchedAndRelinkedSuccessfully",
        };
        const message = typeof responseMessage === "string" && responseMessage.trim() !== ""
            ? translate(messageMap[responseMessage] || responseMessage)
            : translate(fallbackKey);
        // Keep all successful scenarios as success toast for consistency.
        toast.success(message);
    };
    const onSubmit = (formData) => {
        const urlParams = new URLSearchParams(window.location.search);
        const leadId = urlParams.get("id");
        if (leadId) {
            formData.leadId = leadId;
            if (data.leadContactId && data.leadContactId !== "") {
                formData.leadContactId = data.leadContactId;
                formData.id = data.id;
                setSaving(true);
                LeadDetailApi.updateContact(cleanGuidFields(formData))
                    .then((response) => {
                    if (response.status === 200 && response.data != null) {
                        showContactSaveToast(response.data, "UpdatedSuccessfully");
                        onCloseModal();
                        GetLeadContacts();
                        reset(initialFormData);
                    }
                })
                    .finally(() => {
                    setSaving(false);
                });
            }
            else {
                setSaving(true);
                formData.id = (selectedExisting === null || selectedExisting === void 0 ? void 0 : selectedExisting.id) || undefined;
                formData.leadContactId = undefined;
                LeadDetailApi.addContact(cleanGuidFields(formData))
                    .then((response) => {
                    if (response.status === 200 && response.data != null) {
                        showContactSaveToast(response.data, "SavedSuccessfully");
                        onCloseModal();
                        GetLeadContacts();
                        reset(initialFormData);
                    }
                })
                    .finally(() => {
                    setSaving(false);
                });
            }
        }
    };
    useEffect(() => {
        reset(data);
        if (data.id && data.id !== "") {
            setSelectedExisting(null);
            setSearchInputValue("");
        }
    }, [data, reset]);
    const isEditing = data.leadContactId && data.leadContactId !== "";
    const noResultsOptionId = "__no_results__";
    const shouldShowNoResultsOption = hasSearched &&
        !searching &&
        !selectedExisting &&
        searchInputValue.trim().length >= 2 &&
        searchResults.length === 0;
    const dropdownOptions = shouldShowNoResultsOption
        ? [{ id: noResultsOptionId, name: translate("NoContactsFound") }]
        : searchResults;
    return (_jsx(CustomModal, { open: open, onClose: onCloseModal, size: "sm", saving: saving, handleSaveButtonClick: handleSaveButtonClick, heading: translate("AddContactPerson"), saveButtonText: translate("Save"), disabled: false, children: _jsxs(Box, { sx: { px: { xs: 0, sm: 1 }, pb: 2 }, children: [!isEditing && (_jsxs(Box, { sx: {
                        marginTop: "12px",
                        marginBottom: "40px",
                        p: 2.5,
                        borderRadius: "12px",
                        bgcolor: "var(--n-primary-light, #F9F6FF)",
                        border: "1px solid",
                        borderColor: "rgba(19, 9, 34, 0.08)",
                    }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 2.5 }, children: [_jsx(MagnifyingGlass, { size: 18, weight: "bold", color: "#130922" }), _jsx(Typography, { variant: "body2", sx: { fontWeight: 600, color: "var(--n-primary, #130922)" }, children: translate("SearchExistingContact") })] }), _jsx(Autocomplete, { freeSolo: true, open: searchInputValue.trim().length >= 2 && !selectedExisting, openOnFocus: true, options: dropdownOptions, getOptionDisabled: (option) => option.id === noResultsOptionId, getOptionLabel: (option) => {
                                if (typeof option === "string")
                                    return option;
                                if (option.id === noResultsOptionId) {
                                    return option.name || "";
                                }
                                const emailPart = option.email ? " (" + option.email + ")" : "";
                                return (option.name || "") + emailPart;
                            }, inputValue: searchInputValue, onInputChange: (_event, newInputValue, reason) => {
                                setSearchInputValue(newInputValue);
                                if (reason === "reset") {
                                    return;
                                }
                                if (selectedExisting) {
                                    setSelectedExisting(null);
                                }
                                handleSearchContacts(newInputValue);
                            }, onChange: (_event, value) => {
                                if (value && typeof value !== "string") {
                                    handleSelectExistingContact(value);
                                }
                                else {
                                    handleSelectExistingContact(null);
                                }
                            }, loading: searching, renderOption: (props, option) => {
                                if (typeof option === "string")
                                    return null;
                                if (option.id === noResultsOptionId) {
                                    return (_createElement("li", Object.assign({}, props, { key: noResultsOptionId }),
                                        _jsx(Typography, { variant: "body2", sx: { px: 0.5, py: 0.25, color: "text.secondary" }, children: translate("NoContactsFound") })));
                                }
                                const secondaryText = option.email && option.phoneNumber
                                    ? option.email + " " + option.phoneNumber
                                    : option.email || option.phoneNumber || "";
                                return (_createElement("li", Object.assign({}, props, { key: option.id }),
                                    _jsxs(Box, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            py: 0.5,
                                            width: "100%",
                                        }, children: [_jsx(Box, { sx: {
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "50%",
                                                    bgcolor: "var(--n-primary-light, #F9F6FF)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }, children: _jsx(User, { size: 18, color: "#130922" }) }), _jsxs(Box, { sx: { minWidth: 0, flex: 1 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600, lineHeight: 1.3 }, noWrap: true, children: option.name }), secondaryText && (_jsx(Typography, { variant: "caption", noWrap: true, component: "div", sx: {
                                                            color: "text.secondary"
                                                        }, children: secondaryText }))] })] })));
                            }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("SearchByNameOrEmail"), variant: "outlined", fullWidth: true, size: "small", sx: {
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        bgcolor: "#fff",
                                    },
                                }, slotProps: Object.assign(Object.assign({}, params.slotProps), { input: Object.assign(Object.assign({}, params.slotProps.input), { endAdornment: (_jsxs(_Fragment, { children: [searching ? (_jsx(CircularProgress, { color: "inherit", size: 18 })) : null, params.slotProps.input.endAdornment] })) }) }) }))), noOptionsText: translate("NoContactsFound"), filterOptions: (x) => x })] })), !isEditing && (_jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        marginBottom: "36px",
                    }, children: [_jsx(Divider, { sx: { flex: 1 } }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(UserPlus, { size: 16, color: "#888" }), _jsx(Typography, { variant: "caption", sx: { color: "text.secondary", whiteSpace: "nowrap" }, children: translate("AddorUpdateContactPerson") })] }), _jsx(Divider, { sx: { flex: 1 } })] })), _jsx(Box, { sx: { mt: isEditing ? "12px" : 0 }, children: _jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [_jsx("input", Object.assign({ type: "hidden" }, register("id"))), _jsx("input", Object.assign({ type: "hidden" }, register("leadContactId"))), _jsxs(Grid, { container: true, spacing: 2.5, children: [_jsx(Grid, { size: 12, children: _jsx(Controller, { name: "name", control: control, defaultValue: data.name, render: ({ field }) => {
                                                var _a;
                                                return (_jsx(TextField, Object.assign({ label: translate("Name"), variant: "outlined", fullWidth: true }, field, { error: !!errors.name, helperText: (_a = errors.name) === null || _a === void 0 ? void 0 : _a.message, sx: {
                                                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                                                    }, slotProps: {
                                                        input: {
                                                            startAdornment: (_jsx(User, { size: 18, color: "#999", style: { marginRight: 8 } })),
                                                        }
                                                    } })));
                                            } }) }), _jsx(Grid, { size: 12, children: _jsx(Controller, { name: "function", control: control, defaultValue: data.function, render: ({ field }) => (_jsx(TextField, Object.assign({ label: translate("Function"), variant: "outlined", fullWidth: true }, field, { sx: {
                                                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                                                }, slotProps: {
                                                    input: {
                                                        startAdornment: (_jsx(Briefcase, { size: 18, color: "#999", style: { marginRight: 8 } })),
                                                    }
                                                } }))) }) }), _jsx(Grid, { size: {
                                            xs: 12,
                                            sm: 6
                                        }, children: _jsx(Controller, { name: "phoneNumber", control: control, defaultValue: data.phoneNumber, render: ({ field }) => (_jsx(TextField, Object.assign({ label: translate("Telephone"), variant: "outlined", fullWidth: true }, field, { sx: {
                                                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                                                }, slotProps: {
                                                    input: {
                                                        startAdornment: (_jsx(Phone, { size: 18, color: "#999", style: { marginRight: 8 } })),
                                                    }
                                                } }))) }) }), _jsx(Grid, { size: {
                                            xs: 12,
                                            sm: 6
                                        }, children: _jsx(Controller, { name: "email", control: control, defaultValue: data.email, render: ({ field }) => {
                                                var _a;
                                                return (_jsx(TextField, Object.assign({ label: translate("MailAddress"), variant: "outlined", fullWidth: true }, field, { error: !!errors.email, helperText: (_a = errors.email) === null || _a === void 0 ? void 0 : _a.message, sx: {
                                                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                                                    }, slotProps: {
                                                        input: {
                                                            startAdornment: (_jsx(EnvelopeSimple, { size: 18, color: "#999", style: { marginRight: 8 } })),
                                                        }
                                                    } })));
                                            } }) })] }), _jsx("button", { ref: hiddenButtonRef, type: "submit", hidden: true, children: "Submit" })] }) })] }) }));
};
export default CompanyInfoModal;
